import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useApi } from '../hooks/useApi';
import { getStatusClasses } from '../utils/statusUtils';
import LoadingSpinner from './LoadingSpinner';
import ErrorBoundary from './ErrorBoundary';

/**
 * ParrotStatusIndicator Component
 * 
 * Provides visual status indicators for Knowledge Base sync health with aggregate
 * progress display, quick access to detailed status information, and status-based
 * styling and iconography.
 * 
 * Requirements: 6.1, 6.2, 6.3
 */
const ParrotStatusIndicator = ({
    parrotId,
    parrotName,
    className='',
    size = 'medium', // 'small', 'medium', 'large'
    showProgress = true,
    showTooltip = true,
    onClick = null,
    autoRefresh = true,
    refreshInterval = 30000
}) => {
    const { theme } = useTheme();
    const { execute, loading } = useApi();

    // State management
    const [syncStatus, setSyncStatus] = useState(null);
    const [showTooltipContent, setShowTooltipContent] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [error, setError] = useState(null);

    // Fetch sync status for the parrot
    const fetchSyncStatus = useCallback(async () => {
        if (!parrotId) return;

        try {
            setError(null);
            const status = await execute(async (api) => {
                return await api.get(`/api/parrots/${parrotId}/knowledge-base-status`);
            }, { showErrorToast: false });

            setSyncStatus(status);
            setLastUpdated(new Date());
        } catch (error) {
            console.error(`Failed to fetch sync status for parrot ${parrotId}:`, error);
            setError(error.message);
            setSyncStatus(null);
        }
    }, [parrotId, execute]);

    // Initial fetch and periodic refresh
    useEffect(() => {
        fetchSyncStatus();

        if (autoRefresh) {
            const interval = setInterval(fetchSyncStatus, refreshInterval);
            return () => clearInterval(interval);
        }
    }, [fetchSyncStatus, autoRefresh, refreshInterval]);

    // Get status icon based on overall status
    const getStatusIcon = useCallback((status) => {
        // Determine status from aggregate_status if available
        if (syncStatus?.aggregate_status) {
            const { aggregate_status } = syncStatus;
            
            if (aggregate_status.has_failures) {
                return (
                    <svg className="w-full h-full" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                );
            }
            
            if (aggregate_status.in_progress) {
                return (
                    <svg className="w-full h-full animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                );
            }
            
            if (aggregate_status.all_completed && aggregate_status.total_sources > 0) {
                return (
                    <svg className="w-full h-full" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                );
            }
        }

        // Fallback to status-based icons
        switch (status) {
            case 'ready':
            case 'completed':
                return (
                    <svg className="w-full h-full" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                );
            case 'processing':
            case 'queued':
                return (
                    <svg className="w-full h-full animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                );
            case 'failed':
            case 'error':
                return (
                    <svg className="w-full h-full" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                );
            default:
                return (
                    <svg className="w-full h-full" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                );
        }
    }, [syncStatus]);

    // Get status color classes
    const getStatusColorClasses = useCallback((status) => {
        // Determine status from aggregate_status if available
        if (syncStatus?.aggregate_status) {
            const { aggregate_status } = syncStatus;
            
            if (aggregate_status.has_failures) {
                return getStatusClasses(theme, 'error');
            }
            
            if (aggregate_status.in_progress) {
                return getStatusClasses(theme, 'info');
            }
            
            if (aggregate_status.all_completed && aggregate_status.total_sources > 0) {
                return getStatusClasses(theme, 'success');
            }
            
            if (aggregate_status.total_sources === 0) {
                return getStatusClasses(theme, 'secondary');
            }
        }

        // Fallback to status-based colors
        switch (status) {
            case 'ready':
            case 'completed':
                return getStatusClasses(theme, 'success');
            case 'processing':
            case 'queued':
                return getStatusClasses(theme, 'info');
            case 'failed':
            case 'error':
                return getStatusClasses(theme, 'error');
            default:
                return getStatusClasses(theme, 'secondary');
        }
    }, [theme, syncStatus]);

    // Get size classes
    const getSizeClasses = useCallback(() => {
        switch (size) {
            case 'small':
                return {
                    container: 'w-6 h-6',
                    icon: 'w-4 h-4',
                    text: 'text-xs',
                    badge: 'text-xs px-1.5 py-0.5'
                };
            case 'large':
                return {
                    container: 'w-12 h-12',
                    icon: 'w-8 h-8',
                    text: 'text-base',
                    badge: 'text-sm px-3 py-1'
                };
            default: // medium
                return {
                    container: 'w-8 h-8',
                    icon: 'w-6 h-6',
                    text: 'text-sm',
                    badge: 'text-xs px-2 py-1'
                };
        }
    }, [size]);

    // Format progress text
    const getProgressText = useCallback(() => {
        if (!syncStatus || !syncStatus.aggregate_status) return null;

        const { aggregate_status } = syncStatus;
        const { total_sources, ready_sources, pending_sources, failed_sources } = aggregate_status;

        if (total_sources === 0) return 'No sources';
        if (ready_sources === total_sources) return 'All ready';
        if (failed_sources > 0 && pending_sources === 0) return `${failed_sources} failed`;
        if (pending_sources > 0) return `${pending_sources} processing`;
        
        return `${ready_sources} of ${total_sources} ready`;
    }, [syncStatus]);

    // Get tooltip content
    const getTooltipContent = useCallback(() => {
        if (!syncStatus) return 'Status unavailable';

        const { aggregate_status, knowledge_base_sources = [] } = syncStatus;
        
        let content = `${parrotName || 'Parrot'} Knowledge Base Status\n\n`;
        
        if (knowledge_base_sources.length === 0) {
            content += 'No Knowledge Base sources configured';
        } else {
            content += `Total sources: ${aggregate_status.total_sources}\n`;
            if (aggregate_status.ready_sources > 0) content += `✓ Ready: ${aggregate_status.ready_sources}\n`;
            if (aggregate_status.pending_sources > 0) content += `⏳ Processing: ${aggregate_status.pending_sources}\n`;
            if (aggregate_status.failed_sources > 0) content += `✗ Failed: ${aggregate_status.failed_sources}\n`;

            // Show source types breakdown
            if (aggregate_status.source_type_breakdown) {
                content += '\nSource types:\n';
                Object.entries(aggregate_status.source_type_breakdown).forEach(([type, count]) => {
                    content += `• ${type}: ${count}\n`;
                });
            }
        }

        if (lastUpdated) {
            content += `\nLast updated: ${lastUpdated.toLocaleTimeString()}`;
        }

        return content;
    }, [syncStatus, parrotName, lastUpdated]);

    // Handle click events
    const handleClick = useCallback((event) => {
        if (onClick) {
            onClick(event, { parrotId, syncStatus });
        }
    }, [onClick, parrotId, syncStatus]);

    // Handle tooltip visibility
    const handleMouseEnter = useCallback(() => {
        if (showTooltip) {
            setShowTooltipContent(true);
        }
    }, [showTooltip]);

    const handleMouseLeave = useCallback(() => {
        setShowTooltipContent(false);
    }, []);

    const sizeClasses = getSizeClasses();

    // Loading state
    if (loading && !syncStatus) {
        return (
            <div className={`${sizeClasses.container} ${className}`}>
                <LoadingSpinner size="sm" />
            </div>
        );
    }

    // Error state
    if (error && !syncStatus) {
        return (
            <div 
                className={`${sizeClasses.container} ${className} relative`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div className={`${sizeClasses.container} rounded-full flex items-center justify-center ${getStatusColorClasses('error')}`}>
                    {getStatusIcon('error')}
                </div>
                
                {showTooltipContent && (
                    <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg whitespace-pre-line max-w-xs dark:text-white">
                        Failed to load status
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45"></div>
                    </div>
                )}
            </div>
        );
    }

    // No sync status available
    if (!syncStatus) {
        return (
            <div 
                className={`${sizeClasses.container} ${className} relative`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div className={`${sizeClasses.container} rounded-full flex items-center justify-center ${getStatusColorClasses('secondary')}`}>
                    {getStatusIcon('unknown')}
                </div>
                
                {showTooltipContent && (
                    <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg whitespace-pre-line max-w-xs dark:text-white">
                        Status unavailable
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45"></div>
                    </div>
                )}
            </div>
        );
    }

    // Determine overall status from aggregate_status
    const overallStatus = syncStatus?.aggregate_status ? 
        (syncStatus.aggregate_status.has_failures ? 'failed' :
         syncStatus.aggregate_status.in_progress ? 'processing' :
         syncStatus.aggregate_status.all_completed && syncStatus.aggregate_status.total_sources > 0 ? 'ready' :
         'unknown') : 'unknown';
    
    const progressText = getProgressText();

    return (
        <ErrorBoundary>
            <div 
                className={`${className} relative inline-flex items-center space-x-2 ${onClick ? 'cursor-pointer' : ''}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
            >
                {/* Status Icon */}
                <div className={`${sizeClasses.container} rounded-full flex items-center justify-center ${getStatusColorClasses(overallStatus)} flex-shrink-0`}>
                    {getStatusIcon(overallStatus)}
                </div>

                {/* Progress Text */}
                {showProgress && progressText && (
                    <span className={`${sizeClasses.text} text-gray-600 dark:text-gray-400 font-medium`}>
                        {progressText}
                    </span>
                )}

                {/* Tooltip */}
                {showTooltipContent && (
                    <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg whitespace-pre-line max-w-xs dark:text-white">
                        {getTooltipContent()}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45"></div>
                    </div>
                )}
            </div>
        </ErrorBoundary>
    );
};

export default ParrotStatusIndicator;
